/**
 * Auth-Check gegen das Backend.
 *
 * Wir probieren `/api/stat/v1/dimensions` als Auth-Probe.
 * - 200 → eingeloggt, Backend hat Daten
 * - 401 → kein gültiger Cockpit-Session-Cookie
 * - andere Fehler → wahrscheinlich Backend-Problem, NICHT redirecten
 *
 * Statt einen harten Browser-Redirect zu machen, geben wir den Auth-Status
 * als Resultat zurück. App.tsx entscheidet, ob ein freundlicher Hinweis
 * oder das Dashboard angezeigt wird.
 */

import { useQuery } from '@tanstack/react-query';

interface User {
  id: number;
  display_name: string;
  role: string;
  storeIds: number[];
}

export interface AuthState {
  isLoading: boolean;
  authenticated: boolean;
  backendReachable: boolean;
  user: User | null;
  error: string | null;
}

async function probeBackend(): Promise<{
  authenticated: boolean;
  reachable: boolean;
  error: string | null;
}> {
  try {
    const res = await fetch('/api/stat/v1/dimensions', { credentials: 'include' });
    if (res.status === 401) {
      return { authenticated: false, reachable: true, error: null };
    }
    if (!res.ok) {
      return {
        authenticated: false,
        reachable: true,
        error: `Backend-Fehler: HTTP ${res.status}`,
      };
    }
    return { authenticated: true, reachable: true, error: null };
  } catch (e) {
    return {
      authenticated: false,
      reachable: false,
      error: (e as Error).message,
    };
  }
}

export function useAuthCheck(): AuthState {
  const q = useQuery({
    queryKey: ['auth-me'],
    queryFn: probeBackend,
    retry: 0,
    staleTime: 60_000,
  });
  return {
    isLoading: q.isLoading,
    authenticated: !!q.data?.authenticated,
    backendReachable: q.data?.reachable ?? false,
    user: q.data?.authenticated
      ? { id: 0, display_name: 'Cockpit-Nutzer', role: 'user', storeIds: [] }
      : null,
    error: q.data?.error ?? (q.error ? String(q.error) : null),
  };
}
